import { isSubscriptionActive } from './subscription';
import type { SubscriptionState } from '@/types';

/**
 * Premium feature gating (FE half — the BE enforces the same on its gated
 * endpoints). After onboarding a user can browse the app shell for free, but
 * the functional features below require an active subscription. The home
 * dashboard, billing, profile, and the paywall/automation flows stay free so a
 * non-subscriber can still navigate, see their account, and subscribe.
 *
 * Keys match the nav `id` / route segment used in the Sidebar and AppLayout
 * routes (see `src/components/layout/Sidebar.tsx` and `AppLayout.tsx`).
 *
 * Value is the BE `FeatureEnum` key the section needs, or `null` when any
 * active plan suffices.
 */
const PREMIUM_FEATURES: Record<string, string | null> = {
  chat: null,
  'content-review': null,
  calendar: null,
  'knowledge-base': null,
  'topic-trends': 'social_listening',
  'market-news': 'market_news',
  preferences: null,
  channels: null,
  'create-post': null,
  competitors: 'social_listening',
};

/**
 * Whether `page` can be opened. Feature-specific sections need that feature;
 * the rest need any active plan, or a lapsed trial's read-only access.
 */
export function canOpenSection(subscription: SubscriptionState | null, page: string): boolean {
  if (!(page in PREMIUM_FEATURES)) return true;
  const feature = PREMIUM_FEATURES[page];
  if (feature) return subscription?.features?.includes(feature) ?? false;
  return isSubscriptionActive(subscription) || Boolean(subscription?.readOnly);
}

/** Human-readable label for the upgrade prompt, keyed off the nav id. */
const FEATURE_LABELS: Record<string, string> = {
  chat: 'Agent LiLi',
  'content-review': 'Review Queue',
  calendar: 'Calendar',
  'knowledge-base': 'Knowledge Base',
  'topic-trends': 'Social Listening',
  'market-news': 'Market News',
  preferences: 'Brand DNA',
  channels: 'Channels',
  'create-post': 'Create Post',
  competitors: 'Competitors',
};

export function featureLabel(page: string): string {
  return FEATURE_LABELS[page] ?? 'This feature';
}
